import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, InputBase, Tooltip } from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import StyledSidebarKbd from 'common/theme/sidebar/StyledSidebarKbd';
import StyledSidebarSearch from 'common/theme/sidebar/StyledSidebarSearch';

/**
 * Buscador del sidebar: quick-jump sobre los ítems del menú del rol actual
 * (filtro en cliente, NO búsqueda de datos). Enter salta al primer match.
 * Hotkey global "/" (solo desktop): enfoca el campo; en rail colapsado primero
 * expande el panel y luego enfoca.
 */
function SidebarSearch({
  enableHotkey = false,
  iconOnly = false,
  onExpandRequest,
  onQueryChange,
  onSubmit,
  query
}) {
  const { t } = useTranslation(['labels']);
  const inputRef = useRef(null);
  const pendingFocusRef = useRef(false);

  // Tras expandir el rail por "/" o click en la lupa, enfocar el input recién montado.
  useEffect(() => {
    if (!iconOnly && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      inputRef.current?.focus();
    }
  }, [iconOnly]);

  useEffect(() => {
    if (!enableHotkey) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const { target } = event;
      const tag = target?.tagName;
      if (target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }
      event.preventDefault();
      if (iconOnly) {
        pendingFocusRef.current = true;
        onExpandRequest();
        return;
      }
      inputRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableHotkey, iconOnly, onExpandRequest]);

  const placeholder = t('sidebarSearchPlaceholder', {
    ns: 'labels',
    defaultValue: 'Buscar en el menú'
  });

  if (iconOnly) {
    return (
      <Tooltip title={placeholder} placement="right">
        <IconButton
          aria-label={placeholder}
          color="menuItem"
          onClick={() => {
            pendingFocusRef.current = true;
            onExpandRequest();
          }}
          sx={{ alignSelf: 'center', marginBottom: 1.5 }}>
          <Search />
        </IconButton>
      </Tooltip>
    );
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
      return;
    }
    if (event.key === 'Escape') {
      onQueryChange('');
      event.target.blur();
    }
  };

  return (
    // component="label": clickear cualquier parte del recuadro enfoca el input de
    // forma nativa (sin onClick en un Box no interactivo).
    <StyledSidebarSearch component="label">
      <Search fontSize="small" />
      <InputBase
        inputRef={inputRef}
        inputProps={{ 'aria-label': placeholder }}
        placeholder={placeholder}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={handleKeyDown}
        sx={{ color: 'text.primary', flex: 1, fontSize: '0.9rem', minWidth: 0 }}
      />
      {query ? (
        <IconButton
          aria-label={t('clear', { ns: 'labels', defaultValue: 'Limpiar' })}
          edge="end"
          size="small"
          onClick={() => {
            onQueryChange('');
            inputRef.current?.focus();
          }}>
          <Close fontSize="inherit" />
        </IconButton>
      ) : (
        <StyledSidebarKbd aria-hidden="true">/</StyledSidebarKbd>
      )}
    </StyledSidebarSearch>
  );
}

SidebarSearch.propTypes = {
  enableHotkey: PropTypes.bool,
  iconOnly: PropTypes.bool,
  onExpandRequest: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired
};

export default SidebarSearch;
